import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, CheckCircle } from "lucide-react";
import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react"; // ← Fixed import
import { toast } from "react-hot-toast";
import BottomNav from "../components/BottomNav";

const cryptoInfo = {
  btc: {
    network: "Bitcoin Network",
    address: "bc1qzxgqjghvqa89d00uyeftajp2eukxn9uwfrahvr",
    logo: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png?1696501400",
  },
  eth: {
    network: "Ethereum (ERC-20)",
    address: "0xeF8108E2956f85371919d6a089F5b9CA712B6D5b",
    logo: "https://assets.coingecko.com/coins/images/279/small/ethereum.png?1696501410",
  },
  usdt: {
    network: "USDT (ERC-20)",
    address: "0xeF8108E2956f85371919d6a089F5b9CA712B6D5b",
    logo: "https://assets.coingecko.com/coins/images/325/small/Tether.png?1696501661",
  },
  bnb: {
    network: "BNB Smart Chain",
    address: "0xeF8108E2956f85371919d6a089F5b9CA712B6D5b",
    logo: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png?1696501970",
  },
  sol: {
    network: "Solana Network",
    address: "8i7xh1gKkYYKpjpr85TjN9J77GRh4yL9xT23c4MuknFn",
    logo: "https://assets.coingecko.com/coins/images/4128/small/solana.png?1696504756",
  },
   xrp: {
    network: "XRP Ledger",
    address: "rQUmNh23qKDi3Sayy4bZB5ohCjJnGNc4CD",
    tag: "123456788",
    logo: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png?1696501442",
  },
  ltc: {
    network: "Litecoin",
    address: "LTC8tLgjpdeqmfSHzfAHaFEZR3bjf1tzha",
    logo: "https://assets.coingecko.com/coins/images/2/standard/litecoin.png?1696501400",
  },
  usdc: {
    network: "usdc",
    address: "0xeF8108E2956f85371919d6a089F5b9CA712B6D5b",
    logo: "https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694" 
   },
};

export default function FundDepositPage() {
  const { coinId } = useParams();
  const navigate = useNavigate();
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [copiedTag, setCopiedTag] = useState(false);

  const coin = cryptoInfo[coinId];
  if (!coin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
        <p className="text-red-600 font-medium">Invalid cryptocurrency</p>
      </div>
    );
  }

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(coin.address);
      setCopiedAddr(true);
      toast.success("Address copied!");
      setTimeout(() => setCopiedAddr(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const copyTag = async () => {
    try {
      await navigator.clipboard.writeText(coin.tag);
      setCopiedTag(true);
      toast.success("Tag copied!");
      setTimeout(() => setCopiedTag(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-20 pb-24 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">

            {/* Header */}
            <div className="flex items-center mb-8">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <img src={coin.logo} alt={coinId} className="w-8 h-8 object-contain" />
                <h1 className="text-2xl font-bold text-primary">Deposit {coinId.toUpperCase()}</h1>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Send only {coinId.toUpperCase()} to this address.</strong> Sending any other asset will result in permanent loss.
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white rounded-xl shadow-md">
                <QRCodeCanvas value={coin.address} size={180} />
              </div>
            </div>

            {/* Address Box */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Network</p>
              <p className="font-medium text-gray-900">{coin.network}</p>

              <p className="text-sm text-gray-600 mt-4 mb-2">Deposit Address</p>
              <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                <code className="text-xs sm:text-sm break-all font-mono text-gray-800">{coin.address}</code>
                <button
                  onClick={copyAddress}
                  className="ml-2 p-2 rounded hover:bg-gray-100 transition"
                  aria-label="Copy address"
                >
                  {copiedAddr ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                </button>
              </div>

              {coin.tag && (
                <>
                  <p className="text-sm text-gray-600 mt-4 mb-2">Destination Tag</p>
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                    <code className="font-mono text-gray-800">{coin.tag}</code>
                    <button
                      onClick={copyTag}
                      className="ml-2 p-2 rounded hover:bg-gray-100 transition"
                      aria-label="Copy tag"
                    >
                      {copiedTag ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Done Button */}
            <div className="text-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="bg-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-primary/90 transition shadow-lg"
              >
                Done - Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

       <BottomNav /> 
    </>
  );
}